/**
 * Cost By User Component
 * Table showing cost per individual user sorted by total cost
 */

import React, { useState, useMemo } from 'react';
import { DollarSign, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { UserCostData } from '../types';

interface CostByUserProps {
  data: UserCostData[];
  onUserSelect: (userId: number) => void;
}

type SortField = 'email' | 'total_cost' | 'total_queries' | 'simulation_count' | 'chat_count';
type SortDirection = 'asc' | 'desc';

const BRAND_PRIMARY = '#03e8d3';
const INITIAL_DISPLAY_COUNT = 5;

export const CostByUser: React.FC<CostByUserProps> = ({ data, onUserSelect }) => {
  const [sortField, setSortField] = useState<SortField>('total_cost');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showAll, setShowAll] = useState(false);

  const formatCost = (value: number): string => {
    if (value >= 1) {
      return `$${value.toFixed(2)}`;
    }
    if (value >= 0.01) {
      return `$${value.toFixed(3)}`;
    }
    return `$${value.toFixed(4)}`;
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue as string).toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline ml-0.5" />
    ) : (
      <ChevronDown className="w-3 h-3 inline ml-0.5" />
    );
  };

  const totalCost = data.reduce((sum, user) => sum + user.total_cost, 0);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h3 className="text-lg font-semibold text-gray-900">Cost by User</h3>
        </div>
        <div className="flex items-center justify-center h-48 text-gray-500">
          No user cost data available for this time period
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <h3 className="text-lg font-semibold text-gray-900">Cost by User</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {data.length} users
            </span>
          </div>
          <span className="text-lg font-bold" style={{ color: BRAND_PRIMARY }}>
            {formatCost(totalCost)}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('email')}
              >
                User <SortIcon field="email" />
              </th>
              <th
                className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('total_cost')}
              >
                Cost <SortIcon field="total_cost" />
              </th>
              <th
                className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('total_queries')}
              >
                Queries <SortIcon field="total_queries" />
              </th>
              <th
                className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('simulation_count')}
              >
                Sims <SortIcon field="simulation_count" />
              </th>
              <th
                className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('chat_count')}
              >
                Chats <SortIcon field="chat_count" />
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(showAll ? sortedData : sortedData.slice(0, INITIAL_DISPLAY_COUNT)).map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                      {user.name || user.email.split('@')[0]}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{user.email}</div>
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCost(user.total_cost)}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-sm text-gray-600">
                    {user.total_queries.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-sm text-gray-600">{user.simulation_count}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-sm text-gray-600">{user.chat_count}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => onUserSelect(user.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                  >
                    View
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* See More / Show Less */}
      {data.length > INITIAL_DISPLAY_COUNT && (
        <div className="px-4 py-2 border-t border-gray-100">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showAll ? `Show Less` : `See More (${data.length - INITIAL_DISPLAY_COUNT} more)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default CostByUser;
